import React, { useState, useRef } from "react";
import { useClipboard } from "@mantine/hooks";
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  SimpleGrid,
  Accordion,
  Paper,
  Stack,
  MantineProvider,
  Divider,
  Anchor,
  Modal,
  Textarea,
  List,
  Collapse,
  Tooltip,
  Transition,
} from "@mantine/core";
import {
  IconFileSearch,
  IconMailQuestion,
  IconLock,
  IconDownload,
  IconFileText,
  IconPaperclip,
  IconCopy,
  IconFilePencil,
  IconFiles,
  IconLayersDifference,
  IconCircleCheck,
  IconChevronDown,
  IconChevronUp,
  IconMessageCircle,
  IconFileDescription,
  IconTools,
  IconDownloadOff,
} from "@tabler/icons-react";
import { Footer } from "./Footer/Footer";
import { submitFeedback } from "../api";
import { notifications } from "@mantine/notifications";
import { rem } from "@mantine/core";
import { ASSOCIATE_EMAIL } from "../constants/emailConstants.js";
import TrialStatus from "./TrialStatus";

const theme = {
  components: {
    Button: {
      styles: {
        root: {
          transition: "background-color 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(15, 45, 45, 0.75)",
            color: "white",
          },
        },
      },
    },
    Accordion: {
      styles: {
        content: {
          transition: "all 300ms ease",
          backgroundColor: "rgb(255, 255, 255, .75)",
          maxWidth: "100%",
          overflow: "hidden",
        },
        item: {
          width: "100%",
          overflow: "hidden",
          backgroundColor: "rgb(255, 255, 255, .75)",
        },
        control: {
          padding: "1rem",
          backgroundColor: "rgb(255, 255, 255, .75)",
          "&:hover": {
            backgroundColor: "rgba(15, 45, 45, 0.1)",
          },
        },
        panel: {
          // Add this
          maxWidth: "100%",
          overflow: "hidden",
        },
      },
    },
  },
};

const SAMPLE_FILES = {
  convertibleTermSheet: {
    name: "Convertible Corp Seed Term Sheet.docx",
    path: "/admin/static/samples/Socius Demo Convertible Corp. - Term Sheet (Series Seed).docx",
  },
  startupTermSheet: {
    name: "Startup Seed Term Sheet.docx",
    path: "/admin/static/samples/Socius Demo Startup, Inc. - Term Sheet (Series Seed).docx",
  },
  mergerBuyerDraft: {
    name: "Merger Agreement (Buyer Draft).docx",
    path: "/admin/static/samples/Socius Demo Mergers Inc. - Merger Agreement (Buyer Draft).docx",
  },
  mergerSellerComments: {
    name: "Merger Agreement (Seller Comments).docx",
    path: "/admin/static/samples/Socius Demo Mergers Inc. - Merger Agreement (Seller Comments).docx",
  },
  rofr: {
    name: "ROFR Agreement (Series Seed).docx",
    path: "/admin/static/samples/Socius Demo Convertible Corp. - ROFR Agreement (Series Seed).docx",
  },
};

const SAMPLE_TASKS = [
  {
    title: "Draft Legal Documents",
    icon: IconFilePencil,
    description: (
      <Stack gap="xs">
        <Text>
          Quickly get a set of NVCA documents drafted from an attached term
          sheet.
        </Text>
        <Text size="sm" fs="italic" c="dimmed">
          Note: Requests for multiple documents can take up to 20 minutes
        </Text>
      </Stack>
    ),
    emailTemplate: `Dear Socius,

Please draft a set of primary NVCA documents based on the attached terms.

Thanks,`,
    subject: "Draft NVCA Documents",
    sampleFiles: [SAMPLE_FILES.convertibleTermSheet],
  },
  {
    title: "Generate an Issues List",
    icon: IconFiles,
    description:
      "Send two documents and get an AI-powered analysis of key differences.",
    emailTemplate: `Dear Socius,

Please create an issues list from these two docs.

Best,`,
    subject: "Generate Issues List",
    sampleFiles: [
      SAMPLE_FILES.mergerBuyerDraft,
      SAMPLE_FILES.mergerSellerComments,
    ],
  },
  {
    title: "Run a Redline",
    icon: IconLayersDifference,
    description: (
      <Group align="center" gap="xs">
        <Text>Request a high-fidelity comparison redlines powered by</Text>
        <img
          src="/admin/static/litera.svg"
          alt="Litera"
          style={{ height: "16px", verticalAlign: "middle" }}
        />
      </Group>
    ),
    emailTemplate: `Hi Socius,

Please run a redline with the attached, Buyer draft is first.

Thanks,`,
    subject: "Run a Redline",
    sampleFiles: [
      SAMPLE_FILES.mergerBuyerDraft,
      SAMPLE_FILES.mergerSellerComments,
    ],
  },
  {
    title: "Ask a Question",
    icon: IconFileSearch,
    description:
      "Make general inquiries or ask questions about specific attachments.",
    emailTemplate: `Hey - One of the founders wants to transfer 5% of their holdings to a trust for the benefit of her children. Is this permissible under the ROFR agreement (attached)?

Thanks,`,
    subject: "Explain ROFR Provisions",
    sampleFiles: [SAMPLE_FILES.rofr],
  },
];

const featureStyles = {
  feature: {
    width: "100%",
    marginBottom: "2rem",
    textAlign: "center",
    maxWidth: "320px",
    height: "270px",
  },
};

const Dashboard = () => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAllDocs, setShowAllDocs] = useState(false);
  const clipboard = useClipboard();
  const featuresRef = useRef(null);

  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim()) return;

    setSubmitting(true);
    try {
      await submitFeedback(feedbackText);
      setFeedbackOpen(false);
      setFeedbackText("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const feedbackModal = (
    <Modal
      opened={feedbackOpen}
      onClose={() => setFeedbackOpen(false)}
      title="Submit Feedback"
      size="md"
    >
      <Stack spacing="md">
        <Textarea
          placeholder="Share your thoughts and suggestions..."
          minRows={4}
          value={feedbackText}
          onChange={(event) => setFeedbackText(event.currentTarget.value)}
        />
        <Button
          onClick={handleFeedbackSubmit}
          loading={submitting}
          disabled={!feedbackText.trim()}
        >
          Submit Feedback
        </Button>
      </Stack>
    </Modal>
  );

  const renderEmailTemplate = (task) => {
    const mailtoParams = new URLSearchParams({
      subject: task.subject,
      body: task.emailTemplate,
    }).toString();

    const mailtoUrl = `mailto:${ASSOCIATE_EMAIL}?${mailtoParams}`;

    return (
      <>
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "4px",
          }}
        >
          <Group mb="xs" align="center">
            <Text size="sm" c="dimmed">
              To:
            </Text>
            <Tooltip
              label="Clicking will open your email client with a prepared email"
              position="top"
              withArrow
              openDelay={200}
            >
              <Anchor
                href={mailtoUrl}
                size="sm"
                fw={500}
                c="var(--primary-color)"
                style={{ textDecoration: "underline" }}
              >
                {ASSOCIATE_EMAIL}
              </Anchor>
            </Tooltip>
          </Group>
          <Divider my="xs" />
          <Text size="md" style={{ whiteSpace: "pre-wrap" }}>
            {task.emailTemplate}
          </Text>
        </div>
        {task.sampleFiles.length > 0 && (
          <Stack gap="xs">
            <Text size="xs" opacity={0.8} dimmed mb={0}>
              Download sample attachments:
            </Text>
            <Group mt={0} gap="xs">
              {task.sampleFiles.map((file, i) => (
                <Tooltip
                  key={i}
                  label="Click to download sample document"
                  position="top"
                  withArrow
                >
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconDownload size={14} />}
                    component="a"
                    href={file.path}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file.name}
                  </Button>
                </Tooltip>
              ))}
            </Group>
          </Stack>
        )}
        <Button
          mt="md"
          variant="filled"
          style={{ backgroundColor: "rgba(15,45,45,.9)" }}
          leftSection={<IconMessageCircle size={16} />}
          component="a"
          href={mailtoUrl}
        >
          Open your email client
        </Button>
      </>
    );
  };

  return (
    <MantineProvider
      theme={theme}
      styles={{
        global: {
          "body, h1, h2, h3, h4, h5, h6": {
            fontFamily: "DM Sans, sans-serif",
          },
        },
      }}
    >
      <Container size="xl" py="sm">
        <Stack spacing="xl">
          {/* Welcome Section */}
          <Stack gap="xl" align="center">
            <Stack gap="xs" ta="center">
              <Title
                order={1}
                ff={"DM Sans, sans-serif"}
                fw={600}
                size="3rem"
                lts={-2}
                lh="3rem"
              >
                Welcome to Socius
              </Title>
              <Text size="xl" c="dimmed" ff={"DM Sans, sans-serif"} lh="1.5rem">
                The AI-powered legal assistant for lawyers
              </Text>
            </Stack>
            <TrialStatus />

            <Paper p="sm" withBorder radius="md">
              <Stack spacing="lg" maw={650}>
                <Title
                  order={2}
                  ta="center"
                  ff="DM Sans, sans-serif"
                  fw={600}
                  lts={-1}
                  opacity={0.9}
                >
                  How It Works
                </Title>
                <Stack spacing="md">
                  <Group gap="md" align="flex-start">
                    <IconMailQuestion
                      size={28}
                      opacity={0.9}
                      style={{ color: "var(--primary-color)" }}
                    />
                    <Text size="lg" lts={-0.5} opacity={0.9}>
                      Send an email to{" "}
                      <Tooltip
                        label={clipboard.copied ? "Copied to clipboard!" : ""}
                        position="top"
                        withArrow
                        opened={clipboard.copied}
                      >
                        <Anchor
                          onClick={() => clipboard.copy(ASSOCIATE_EMAIL)}
                          fw={700}
                          lts={-1}
                          c="var(--primary-color)"
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          {ASSOCIATE_EMAIL}
                        </Anchor>
                      </Tooltip>{" "}
                      with your request.
                    </Text>
                  </Group>
                  <Group gap="md" align="flex-start">
                    <IconPaperclip
                      size={28}
                      opacity={0.9}
                      style={{ color: "var(--primary-color)" }}
                    />
                    <Text size="lg" lts={-0.5} opacity={0.9}>
                      Attach any relevant documents you'd like to include.
                    </Text>
                  </Group>
                  <Group gap="md" align="flex-start">
                    <IconFileText
                      size={28}
                      opacity={0.9}
                      style={{ color: "var(--primary-color)" }}
                    />
                    <Text size="lg" lts={-0.5} opacity={0.9}>
                      Receive comprehensive AI-powered responses directly to
                      your inbox.
                    </Text>
                  </Group>
                </Stack>
              </Stack>
            </Paper>
          </Stack>

          {/* Features Section */}
          <section ref={featuresRef}>
            <Container size="xl">
              <Title
                order={2}
                ta="center"
                mt="xl"
                style={{
                  fontFamily: '"DM Sans", sans-serif',
                  letterSpacing: "-1px",
                }}
              >
                Features and Capabilities
              </Title>
              <Text mb="md" ta="center">
                Socius is built to integrate seamlessly into your existing
                workflow.
              </Text>

              <Group justify="center" mt="xl">
                {[
                  {
                    icon: IconFileDescription,
                    title: "Draft legal documents quickly",
                    description:
                      "We use smart precedents to ensure consistent, quality drafting",
                  },
                  {
                    icon: IconTools,
                    title: "Process attachments easily",
                    description:
                      "Convert documents to PDFs, run comparisons, summarize documents, and more.",
                  },
                  {
                    icon: IconDownloadOff,
                    title: "No downloads required",
                    description:
                      "Send emails directly from your inbox, no installation needed.",
                  },
                ].map((feature, index) => (
                  <Card
                    withBorder
                    shadow="lg"
                    padding="lg"
                    className="feature-card"
                    style={{
                      ...featureStyles.feature,
                    }}
                  >
                    <Card.Section p="lg">
                      <feature.icon
                        size={64}
                        stroke={1.5}
                        color="rgba(15,45,45,.9)"
                      />
                    </Card.Section>
                    <Title order={4} mt="sm">
                      {feature.title}
                    </Title>
                    <Text mt="sm">{feature.description}</Text>
                  </Card>
                ))}
              </Group>
            </Container>
          </section>

          {/* Sample Requests Section */}
          <section>
            <Title
              order={2}
              ta="center"
              mt="xl"
              style={{
                fontFamily: '"DM Sans", sans-serif',
                letterSpacing: "-1px",
              }}
            >
              Get started with some examples
            </Title>
            <Text mb="md" ta="center">
              Below are some sample emails and attachments that you can download
              to try your first requests.
            </Text>

            <Divider maw={700} mx="auto" />
            <Accordion variant="separated" maw={700} mx="auto">
              {SAMPLE_TASKS.map((task, index) => (
                <Accordion.Item key={index} value={task.title}>
                  <Accordion.Control
                    icon={
                      <task.icon
                        style={{
                          color: "rgba(15,45,45,0.75)",
                          width: rem(24),
                          height: rem(24),
                        }}
                      />
                    }
                  >
                    <Title order={3}>{task.title}</Title>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack spacing="md">
                      <Text size="sm" dimmed>
                        {task.description}
                      </Text>
                      {renderEmailTemplate(task)}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </section>

          <Divider maw={700} mx="auto" />

          {/* Document Types Section */}
          <section>
            <Title
              order={2}
              ta="center"
              mt="xl"
              style={{
                fontFamily: '"DM Sans", sans-serif',
                letterSpacing: "-1px",
              }}
            >
              Document Types
            </Title>
            <Text mb="sm" ta="center">
              Socius can generate first drafts for a comprehensive range of
              legal documents.
            </Text>

            <Group justify="center">
              <Button
                variant="transparent"
                onClick={() => setShowAllDocs(!showAllDocs)}
                leftSection={
                  showAllDocs ? (
                    <IconChevronUp size={16} />
                  ) : (
                    <IconChevronDown size={16} />
                  )
                }
              >
                {showAllDocs ? "Show Less" : "View All Document Types"}
              </Button>
            </Group>

            <Collapse in={showAllDocs}>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mt="lg">
                <Paper p="md" radius="md" withBorder>
                  <Stack>
                    <Title order={3} size="h4">
                      Primary NVCA Documents
                    </Title>
                    <List
                      spacing="xs"
                      size="sm"
                      center
                      icon={
                        <IconCircleCheck
                          style={{
                            width: rem(16),
                            height: rem(16),
                            color: "var(--primary-color)",
                          }}
                        />
                      }
                    >
                      <List.Item>Charter</List.Item>
                      <List.Item>Stock Purchase Agreement</List.Item>
                      <List.Item>Investors' Rights Agreement</List.Item>
                      <List.Item>Voting Agreement</List.Item>
                      <List.Item>
                        Right of First Refusal and Co-Sale Agreement
                      </List.Item>
                      <List.Item>Management Rights Letter</List.Item>
                    </List>
                  </Stack>
                </Paper>

                <Paper p="md" radius="md" withBorder>
                  <Stack>
                    <Title order={3} size="h4">
                      Supporting Documents
                    </Title>
                    <List
                      spacing="xs"
                      size="sm"
                      center
                      icon={
                        <IconCircleCheck
                          style={{
                            width: rem(16),
                            height: rem(16),
                            color: "var(--primary-color)",
                          }}
                        />
                      }
                    >
                      <List.Item>Term Sheet</List.Item>
                      <List.Item>SAFE Agreements</List.Item>
                      <List.Item>Side Letters</List.Item>
                      <List.Item>Due Diligence Request Lists</List.Item>
                      <List.Item>Board Minutes</List.Item>
                      <List.Item>Working Group Lists</List.Item>
                    </List>
                  </Stack>
                </Paper>

                <Paper p="md" radius="md" withBorder>
                  <Stack>
                    <Title order={3} size="h4">
                      Schedules & Exhibits
                    </Title>
                    <List
                      spacing="xs"
                      size="sm"
                      center
                      icon={
                        <IconCircleCheck
                          style={{
                            width: rem(16),
                            height: rem(16),
                            color: "var(--primary-color)",
                          }}
                        />
                      }
                    >
                      <List.Item>Stockholder Schedules</List.Item>
                      <List.Item>QSBS Documentation</List.Item>
                      <List.Item>Due Diligence Materials</List.Item>
                      <List.Item>Exhibits & Attachments</List.Item>
                    </List>
                  </Stack>
                </Paper>

                <Paper p="md" radius="md" withBorder>
                  <Stack>
                    <Title order={3} size="h4">
                      Certificates & Signature Pages
                    </Title>
                    <List
                      spacing="xs"
                      size="sm"
                      center
                      icon={
                        <IconCircleCheck
                          style={{
                            width: rem(16),
                            height: rem(16),
                            color: "var(--primary-color)",
                          }}
                        />
                      }
                    >
                      <List.Item>Corporate Certificates</List.Item>
                      <List.Item>Letters and Letter Agreements</List.Item>
                      <List.Item>Joinder Agreements</List.Item>
                      <List.Item>Transaction Signature Pages</List.Item>
                    </List>
                  </Stack>
                </Paper>
              </SimpleGrid>
            </Collapse>
          </section>

          {/* Support and Links Section */}
          <section>
            <Title
              order={2}
              ta="center"
              mt="xl"
              style={{
                fontFamily: '"DM Sans", sans-serif',
                letterSpacing: "-1px",
              }}
            >
              Support and Resources
            </Title>
            <Text mb="xl" ta="center">
              Get help, share feedback, and learn more about our platform.
            </Text>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" ta="center">
              <Card withBorder miw={300}>
                <Stack gap="lg" align="center">
                  <div>
                    <Text fw={600}>Feedback and support</Text>
                    <Text size="sm">
                      We're here to help you get the most out of Socius.
                    </Text>
                  </div>
                  <Button
                    maw="400px"
                    onClick={() => setFeedbackOpen(true)}
                    leftSection={<IconMessageCircle size={16} />}
                  >
                    Get in touch
                  </Button>
                </Stack>
              </Card>

              <Card withBorder miw={300}>
                <Stack gap="lg" align="center">
                  <div>
                    <Text fw={600}>Learn more</Text>
                    <Text size="sm">
                      Explore our security features and AI principles.
                    </Text>
                  </div>
                  <Group position="center" justify="center">
                    <Button
                      variant="light"
                      color="gray"
                      leftSection={<IconLock size={16} />}
                      component="a"
                      href="/security"
                    >
                      Security Center
                    </Button>
                    <Button
                      variant="light"
                      color="gray"
                      leftSection={<IconFileSearch size={16} />}
                      component="a"
                      href="/responsible-ai"
                    >
                      Responsible AI
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </SimpleGrid>
          </section>
        </Stack>

        {feedbackModal}
      </Container>
      {Footer()}
    </MantineProvider>
  );
};

export default Dashboard;
